import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Input,
  Select,
  Tooltip,
  Tag,
  Table,
  Popconfirm,
  Modal,
  Form,
  Card,
  Divider,
  Upload,
  TimePicker,
  Drawer,
  Tabs,
  Descriptions,
  Badge,
  Timeline,
  Progress,
  Empty,
  Spin,
  Row,
  Col,
  Statistic,
  Alert,
  DatePicker,
  InputNumber,
  Space,
  Pagination,
} from 'antd';
import {
  SyncOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
  EyeOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import ReusableSearchInput from '@/components/ReusableSearch';
import TableContainer from '@/components/TableContainer';
import {
  get_departments,
  get_roles,
  get_designations,
  add_employee_user,
  get_employee_user_list,
  toggle_employee_status,
  unlock_employee,
  add_role,
  add_department,
  add_designation,
  get_shift,
  get_warehouse_area,
  bp_add_location_data,
  bp_save_employee_shift,
} from '@/utils/api';
import { toast } from 'react-toastify';
import { ShieldCheck } from 'lucide-react';
import { useSelector } from 'react-redux';
import { CheckCircle2, XCircle } from 'lucide-react';
import { accessControlUtils } from '@/components/AccessControlUtils';
import { useTranslation } from 'react-i18next';
import UserCertificateInfo from '@/components/UserCertificateInfo';
import UserSignatureHistory from '@/components/UserSignatureHistory';
import useTableConfig from '@/hooks/useTableConfig';
import TableColumnConfig from '@/components/TableColumnConfig';
import Axios from 'axios';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import { formatDate, toAppMoment } from '@/utils/datetime';
import {
  get_employee_trainings,
  get_training_courses,
  get_expiring_trainings,
  certify_employee_training,
} from '../../../utils/training_api';

// Status color mapping for training records
const statusColors = {
  COMPLETED: 'success',
  IN_PROGRESS: 'processing',
  SCHEDULED: 'default',
  FAILED: 'error',
  EXPIRED: 'warning',
  CANCELLED: 'default',
};

const UserConfig2 = ({ isEmbedded } = {}) => {
  const { isDark } = useTheme();
  const formRef = useRef();
  const certificateInputRef = useRef();
  const [searchvalue, setSearchvalue] = useState('');
  const user_Configuration_access = accessControlUtils('User Management');

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const labelColor = isDark ? defaultColors.palette.gray[300] : '#374151';
  const tableBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const tableHeaderBgColor = isDark ? defaultColors.palette.gray[700] : '#fafafa';
  const tableRowHoverBg = isDark ? defaultColors.palette.gray[700] : '#f0f7ff';

  // STATES OF INPUT FIELDS
  const [first_name, setfirst_name] = useState('');
  const [last_name, setlast_name] = useState('');
  const [email, setEmail] = useState('');
  const [contact, setContact] = useState('');
  const [department_id, setDepartment] = useState();
  const [location, set_location] = useState([]);
  const [designation_id, setDesignation] = useState();
  const [shift, setShift] = useState([]);
  const [group_id, setGroup] = useState([]);
  const [role_id, setRole] = useState([]);
  const [userId, setuserId] = useState('');
  const selectedModule = useSelector((state) => state.moduleView);

  // Certificate upload states
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificateRefresh, setCertificateRefresh] = useState(0);

  // INPUT HANDLERS
  const handlefirst_name = (e) => {
    setfirst_name(e.target.value);
    if (firstNameError) setFirstNameError('');
  };
  const handlelast_name = (e) => {
    setlast_name(e.target.value);
    if (lastNameError) setLastNameError('');
  };
  const handleEmail = (e) => {
    setEmail(e.target.value);
  };
  const handleContact = (e) => {
    setContact(e.target.value);
    if (contactError) setContactError('');
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleCancel = () => {
    setIsModalOpen(false);
    set_add_name('');
    set_add_index('');
  };

  const [add_index, set_add_index] = useState('');
  const [add_name, set_add_name] = useState('');
  const [add_shift, set_add_shift] = useState([]);

  const handle_add_icon = (index) => {
    setIsModalOpen(true);
    set_add_index(index);
  };
  const handle_add_name = (e) => {
    set_add_name(e.target.value);
  };

  const handle_add_shift = (val) => {
    set_add_shift(val);
  };

  const handleEdit = (record) => {
    set_edit_val(true);
    set_employee_id(record?.employee_id);
    setfirst_name(record.employee_name?.split(' ')[0] || '');
    // Last name may be absent (single-word employee name) — default to '' so the
    // controlled input never becomes undefined and the native `required` check
    // doesn't silently block the Update submit.
    setlast_name(record.employee_name?.split(' ').slice(1).join(' ') || '');
    setuserId(record?.username);
    setEmail(record?.email);
    setContact(record?.contact != null ? String(record.contact) : '');
    setDepartment(record?.department_id);
    setDesignation(record?.designation_id);
    try {
      const raw = record?.role_id;
      const parsed = Array.isArray(raw)
        ? raw.map(Number)
        : JSON.parse(String(raw).replace(/'/g, '"')).map(Number);
      setRole(parsed);
    } catch {
      setRole([]);
    }
    setGroup([]);
    setShift(record?.shifts?.length ? record.shifts : record?.shift_id ? [record.shift_id] : []);
    set_location(
      record?.locations?.length ? record.locations : record?.location_id ? [record.location_id] : []
    );
    // Clear certificate file when editing
    setCertificateFile(null);
    if (certificateInputRef.current) {
      certificateInputRef.current.value = null;
    }
    setuserShow(true);
  };
  const [edit_val, set_edit_val] = useState(false);
  const formBgColor = isDark
    ? defaultColors.palette.gray[750] || defaultColors.palette.gray[700]
    : '#f8faff';
  const accentColor = edit_val ? '#f59e0b' : '#3b82f6';
  const accentBg = edit_val
    ? isDark
      ? 'rgba(245,158,11,0.08)'
      : '#fffbeb'
    : isDark
    ? 'rgba(59,130,246,0.08)'
    : '#eff6ff';

  const [refreshValue, setRefreshValue] = useState(true);

  // E-Signature state
  const [showSignatureHistory, setShowSignatureHistory] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleViewSignatures = (user) => {
    setSelectedUser(user);
    setShowSignatureHistory(true);
  };

  // Training drawer state
  const [trainingDrawerVisible, setTrainingDrawerVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [trainingRecords, setTrainingRecords] = useState([]);
  const [trainingStats, setTrainingStats] = useState(null);
  const [trainingDrawerLoading, setTrainingDrawerLoading] = useState(false);
  // Training certification modal state
  const [certificationModalVisible, setCertificationModalVisible] = useState(false);
  const [certificationEmployee, setCertificationEmployee] = useState(null);
  const [certificationForm] = Form.useForm();

  // Handle view employee training details
  // const handleViewTrainings = async (employee) => {
  //   setSelectedEmployee(employee);
  //   setTrainingDrawerVisible(true);
  //   setTrainingDrawerLoading(true);

  //   try {
  //     await get_employee_trainings(employee.employee_id, (res) => {
  //       if (res?.status === 'success') {
  //         setTrainingRecords(res?.data?.training_records || []);
  //         setTrainingStats(res?.data?.statistics || null);
  //       } else {
  //         toast.warning('Failed to fetch training records');
  //       }
  //       setTrainingDrawerLoading(false);
  //     });
  //   } catch (e) {
  //     console.error('Error fetching employee trainings:', e);
  //     setTrainingDrawerLoading(false);
  //   }
  // };

  // Close training drawer
  const handleTrainingDrawerClose = () => {
    setTrainingDrawerVisible(false);
    setSelectedEmployee(null);
    setTrainingRecords([]);
    setTrainingStats(null);
  };

  // Handle training certification
  const handleCertifyTraining = (employee) => {
    setCertificationEmployee(employee);
    setCertificationModalVisible(true);
    certificationForm.resetFields();
  };

  const handleCertificationSubmit = async () => {
    try {
      const values = await certificationForm?.validateFields();

      const certificationData = {
        employee_id: certificationEmployee?.employee_id,
        course_id: values.course_id,
        training_date: values.training_date?.format('YYYY-MM-DD'),
        completion_date: values.completion_date?.format('YYYY-MM-DD'),
        assessment_score: values.assessment_score,
        certificate_number: values.certificate_number,
        remarks: values.remarks,
        status: 'COMPLETED', // Default status for certification
      };

      // Call certification API
      await certify_employee_training(certificationData, (res) => {
        console.log(res);

        if (res.status === 'success') {
          toast.success(res.message || 'Training certification added successfully');
          setCertificationModalVisible(false);
          certificationForm.resetFields();
          setRefreshValue(!refreshValue);
          toast.success(res?.message);
        } else {
          toast.error(res.message || 'Failed to certify training');
        }
      });
    } catch (error) {
      console.error('Certification failed:', error);
      toast.error('Failed to certify training. Please try again.');
    }
  };

  const exists_data_column = [
    {
      dataIndex: 'first_name',
      align: 'center',
      title: 'First Name',
    },
    {
      dataIndex: 'last_name',
      align: 'center',
      title: 'Last Name',
    },
    {
      dataIndex: 'email',
      align: 'center',
      title: 'Email',
    },
    {
      dataIndex: 'contact',
      align: 'center',
      title: 'Contact',
    },
    {
      dataIndex: 'msg',
      title: 'Error Message',
    },
  ];

  const success_add_User = async (res) => {
    if (res.status === 'success') {
      toast.success(res.message);
      if (res?.elready_exists_data?.length > 0) {
        await error(res?.elready_exists_data);
      }

      // If certificate file was uploaded, upload it now
      const created_employee_id = res?.employee_id || employee_id;
      if (certificateFile && created_employee_id) {
        await uploadCertificate(created_employee_id);
      }

      resetForm();
      setRefreshValue(!refreshValue);
      set_refresh(!refresh);
      set_edit_val(false);
      setuserShow(false);
      setCertificateRefresh((prev) => prev + 1);
      formRef?.current?.reset();
    } else {
      toast.error(res.message);
      setuserShow(false);
      set_edit_val(false);
      resetForm();
      formRef?.current?.reset();
    }
  };

  const uploadCertificate = async (emp_id) => {
    try {
      const formData = new FormData();
      formData.append('certificate_file', certificateFile);
      formData.append('employee_id', emp_id);
      formData.append('common_name', `${first_name} ${last_name}`);

      const token = localStorage.getItem('Token');
      const response = await Axios.post('/bp/upload_user_certificate', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status === 'success') {
        toast.success('Certificate uploaded successfully');
      } else {
        toast.warning(response.data.message || 'Failed to upload certificate');
      }
    } catch (error) {
      console.error('Error uploading certificate:', error);
      toast.error('Failed to upload certificate');
    }
  };

  const error = (exists_data) => {
    Modal.error({
      title: 'Error Message',
      content: (
        <Table
          size="small"
          bordered
          pagination={false}
          columns={exists_data_column}
          dataSource={exists_data}
        />
      ),
      width: '40%',
      okText: 'Close',
    });
  };

  const [employee_id, set_employee_id] = useState('');

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // Required-field validation. Every invalid field's inline error message
    // (shown below that field) is set in one pass, then the submit stops if
    // any field failed. All fields are required except the E-Signature Certificate.
    let hasError = false;

    if (!userId?.trim()) {
      setUserIdError('Please enter User Id.');
      hasError = true;
    } else {
      setUserIdError('');
    }
    if (!first_name?.trim()) {
      setFirstNameError('Please enter First Name.');
      hasError = true;
    } else {
      setFirstNameError('');
    }
    if (!last_name?.trim()) {
      setLastNameError('Please enter Last Name.');
      hasError = true;
    } else {
      setLastNameError('');
    }
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email || '')) {
      setEmailError('Enter valid email');
      hasError = true;
    } else {
      setEmailError('');
    }
    if (!contact?.trim()) {
      setContactError('Please enter Phone Number.');
      hasError = true;
    } else if (!/^\d{10}$/.test(contact.trim())) {
      setContactError('Phone Number must be exactly 10 digits.');
      hasError = true;
    } else {
      setContactError('');
    }
    if (!location?.length) {
      setLocationError('Please select at least one location.');
      hasError = true;
    } else {
      setLocationError('');
    }
    if (!department_id) {
      setDepartmentError('Please select a department.');
      hasError = true;
    } else {
      setDepartmentError('');
    }
    if (!designation_id) {
      setDesignationError('Please select a designation.');
      hasError = true;
    } else {
      setDesignationError('');
    }
    if (!role_id?.length) {
      setRoleError('Please select at least one role.');
      hasError = true;
    } else {
      setRoleError('');
    }
    if (!shift?.length) {
      setShiftError('Please select a shift.');
      hasError = true;
    } else {
      setShiftError('');
    }

    if (hasError) return;

    const user_data = edit_val
      ? {
          username: userId,
          first_name: first_name,
          last_name: last_name,
          email: email,
          contact: contact,
          department_id: department_id,
          group_id: group_id.join(','),
          designation_id: designation_id,
          employee_id: employee_id,
          role_id: role_id.join(','),
          location_id: location,
          shift_id: shift,
        }
      : {
          first_name: first_name,
          last_name: last_name,
          email: email,
          username: userId,
          contact: contact,
          department_id: department_id,
          group_id: group_id.join(','),
          designation_id: designation_id,
          role_id: role_id.join(','),
          location_id: location,
          shift_id: shift,
        };

    try {
      const maybePromise = add_employee_user(user_data, success_add_User);
      if (maybePromise && maybePromise.catch) {
        maybePromise.catch((err) => {
          console.error('add_employee_user failed', err);
          toast.error('Failed to save user (network/server error).');
        });
      }
    } catch (err) {
      console.error('add_employee_user threw', err);
      toast.error('Failed to save user.');
    }
  };
  const resetForm = () => {
    setfirst_name('');
    setlast_name('');
    setuserId('');
    setEmail('');
    setContact('');
    setDepartment();
    setDesignation();
    setRole([]); //Multiple
    setGroup([]);
    setShift([]);
    set_location([]);
    setRole([]);
    setCertificateFile(null);
    // Clear file input
    if (certificateInputRef.current) {
      certificateInputRef.current.value = null;
    }
    setUserIdError('');
    setFirstNameError('');
    setLastNameError('');
    setEmailError('');
    setContactError('');
    setLocationError('');
    setDepartmentError('');
    setDesignationError('');
    setRoleError('');
    setShiftError('');
  };

  // USEEFFECTS
  const [department_list, set_department_list] = useState([]);
  const [shift_list, set_shift_list] = useState([]);
  const [original_role_list, set_original_role_list] = useState([]);
  const [location_list, set_location_list] = useState([]);
  const [designation_list, set_designation_list] = useState([]);
  const [employees_list, set_employees_list] = useState([]);
  const [refresh, set_refresh] = useState(true);
  const { t } = useTranslation();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pageSize, setPageSize] = useState(15);

  // Admin notification state
  const [globalExpiringTrainings, setGlobalExpiringTrainings] = useState([]);
  const [showExpiringModal, setShowExpiringModal] = useState(false);

  // Training courses list
  const [trainingCoursesList, setTrainingCoursesList] = useState([]);

  const success_employee_user_list = (res) => {
    if (res?.status === 'success' && res?.data) {
      set_employees_list(res?.data);
      setTotalUsers(res?.obj_count || res?.user_count || res?.data?.length || 0);
    } else {
      set_employees_list(res?.data || []);
      setTotalUsers(res?.data?.length || 0);
    }
  };

  const success_get_location = (res) => {
    set_location_list(res.data.data);
  };

  const success_set_department = (res) => {
    set_department_list(res.data.departdata);
  };

  const success_get_shift = (res) => {
    set_shift_list(res.data.data);
  };

  const success_set_designation_list = (res) => {
    set_designation_list(res.data.data);
  };
  const success_set_original_role_list = (res) => {
    set_original_role_list(res.data.data);
  };

  const success_get_expiring_trainings = (res) => {
    if (res?.status === 'success') {
      setGlobalExpiringTrainings(res?.data || []);
    }
  };

  const success_get_training_courses = (res) => {
    if (res?.status === 'success') {
      setTrainingCoursesList(res?.data || []);
    }
  };

  const get_lists = async (page = 1) => {
    try {
      await get_employee_user_list(success_employee_user_list, { page_number: page });
      await get_departments(success_set_department);
      await get_shift(success_get_shift);
      await get_warehouse_area(success_get_location);
      await get_designations(success_set_designation_list);
      await get_roles(success_set_original_role_list);
      await get_expiring_trainings({ days: 28 }, success_get_expiring_trainings); // 4 weeks
      await get_training_courses(success_get_training_courses);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    get_lists(currentPage);
  }, [refreshValue, currentPage]);

  // Toggle user active/inactive
  const handleToggleStatus = (record) => {
    toggle_employee_status(record.employee_id, (res) => {
      if (res.status === 'success') {
        toast.success(
          res.message || `User ${record.is_active ? 'deactivated' : 'activated'} successfully`
        );
        setRefreshValue(!refreshValue);
      } else {
        toast.error(res.message || 'Failed to update user status');
      }
    });
  };

  // Unlock a locked account
  const handleUnlockUser = (employee_id) => {
    unlock_employee(employee_id, (res) => {
      if (res.status === 'success') {
        toast.success(res.message || 'Account unlocked successfully');
        setRefreshValue(!refreshValue);
      } else {
        toast.error(res.message || 'Failed to unlock account');
      }
    });
  };

  const [detailRecord, setDetailRecord] = useState(null);

  const detailsColumn = [
    {
      title: 'Details',
      key: 'details',
      dataIndex: 'details',
      width: 80,
      align: 'center',
      fixed: 'right',
      defaultVisible: true,
      render: (_, record) => (
        <Tooltip title="View Details">
          <Button
            type="text"
            size="small"
            icon={<Icons.Eye className="w-5 h-5" />}
            className="text-teal-600 hover:text-teal-800 hover:bg-teal-50"
            onClick={() => setDetailRecord(record)}
          />
        </Tooltip>
      ),
    },
  ];

  const actionColumn =
    user_Configuration_access?.update_access || user_Configuration_access?.delete_access
      ? [
          {
            title: 'Action',
            key: 'action',
            dataIndex: 'action',
            width: 120,
            align: 'center',
            fixed: 'right',
            defaultVisible: true,
            render: (_, record) => {
              return (
                <Space size="small" className="flex justify-center">
                  {user_Configuration_access?.update_access && (
                    <Tooltip title="Edit User">
                      <Button
                        type="text"
                        size="small"
                        onClick={() => handleEdit(record)}
                        className="flex items-center justify-center text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        icon={<Icons.Pencil className="w-5 h-5" />}
                      />
                    </Tooltip>
                  )}
                  {user_Configuration_access?.delete_access &&
                    (() => {
                      const isLocked = !!record.locked_until;
                      const isActive = record.is_active !== false;
                      return (
                        <>
                          {isLocked && (
                            <Popconfirm
                              title="Unlock Account"
                              description={
                                <div className="max-w-xs">
                                  <p className="font-semibold mb-1">
                                    Unlock <strong>{record.employee_name}</strong>'s account?
                                  </p>
                                  <p className="text-xs text-orange-600 mt-1">
                                    This will allow the user to log in again.
                                  </p>
                                </div>
                              }
                              onConfirm={() => handleUnlockUser(record.employee_id)}
                              okText="Unlock"
                              cancelText="Cancel"
                              okButtonProps={{
                                style: { backgroundColor: '#f97316', borderColor: '#f97316' },
                              }}
                            >
                              <Tooltip title="Unlock Account">
                                <Button
                                  type="text"
                                  size="small"
                                  className="flex items-center justify-center text-orange-500 hover:text-orange-700 hover:bg-orange-50"
                                  icon={<Icons.Unlock className="w-5 h-5" />}
                                />
                              </Tooltip>
                            </Popconfirm>
                          )}
                          <Popconfirm
                            title={isActive ? 'Deactivate User' : 'Activate User'}
                            description={
                              <div className="max-w-xs">
                                <p className="font-semibold mb-1">
                                  {isActive ? 'Deactivate' : 'Activate'}{' '}
                                  <strong>{record.employee_name}</strong>?
                                </p>
                                {isActive && (
                                  <p className="text-xs text-red-600 mt-1">
                                    Deactivated users cannot log in.
                                  </p>
                                )}
                              </div>
                            }
                            onConfirm={() => handleToggleStatus(record)}
                            okText={isActive ? 'Deactivate' : 'Activate'}
                            cancelText="Cancel"
                            okButtonProps={{ danger: isActive }}
                          >
                            <Tooltip title={isActive ? 'Deactivate User' : 'Activate User'}>
                              <Button
                                type="text"
                                size="small"
                                className={`flex items-center justify-center ${
                                  isActive
                                    ? 'text-red-600 hover:text-red-800 hover:bg-red-50'
                                    : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                                }`}
                                icon={
                                  isActive ? (
                                    <Icons.UserX className="w-5 h-5" />
                                  ) : (
                                    <Icons.UserCheck className="w-5 h-5" />
                                  )
                                }
                              />
                            </Tooltip>
                          </Popconfirm>
                        </>
                      );
                    })()}
                </Space>
              );
            },
          },
        ]
      : [];

  // --------------------------------------------------------------------------------------

  const searchInput = useRef(null);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };
  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({
                closeDropdown: false,
              });
            }}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => text,
  });

  // ----------------------------------------------------------------------------

  const allColumns = [
    {
      dataIndex: 'username',
      width: 120,
      title: 'User Id',
      key: 'username',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      searchable: true,
    },
    {
      dataIndex: 'employee_name',
      width: 180,
      title: 'Name',
      key: 'employee_name',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      searchable: true,
    },
    {
      dataIndex: 'contact',
      width: 130,
      title: 'Phone',
      key: 'contact',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      searchable: true,
    },
    {
      dataIndex: 'email',
      width: 220,
      title: 'Email',
      key: 'email',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      searchable: true,
    },
    {
      title: 'Status',
      key: 'account_status',
      dataIndex: 'account_status',
      width: 120,
      align: 'center',
      defaultVisible: true,
      render: (_, record) => {
        const isLocked = record.locked_until && new Date(record.locked_until) > new Date();
        if (isLocked) {
          return <Tag color="orange">Locked</Tag>;
        }
        return record.is_active === false ? (
          <Tag color="default">Inactive</Tag>
        ) : (
          <Tag color="success">Active</Tag>
        );
      },
    },
    {
      dataIndex: 'location',
      width: 120,
      title: 'Location',
      key: 'location',
      align: 'center',
      defaultVisible: false,
      sortable: true,
      sortType: 'string',
      searchable: true,
    },
    {
      dataIndex: 'department',
      width: 130,
      title: 'Department',
      key: 'department',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      searchable: true,
    },
    {
      dataIndex: 'designation',
      width: 140,
      align: 'center',
      title: 'Designation',
      key: 'designation',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      searchable: true,
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      key: 'roles',
      width: 200,
      defaultVisible: true,
      sortable: false,
      render: (_, record, index) => {
        const role_names = Array.isArray(record.role_name)
          ? record.role_name
          : JSON.parse(record.role_name.replace(/'/g, '"'));

        return (
          <div className="flex flex-wrap gap-1">
            {role_names && role_names?.map((item, i) => <Tag key={i}>{item}</Tag>)}
          </div>
        );
      },
    },
    {
      title: 'Is Certified',
      dataIndex: 'is_certified_employee',
      key: 'is_certified',
      width: 150,
      align: 'center',
      defaultVisible: true,
      searchable: true,
      render: (_, record) => {
        // Check if user has valid certificate based on UserCertificateInfo logic
        // For now, we'll show if they have certificate_fingerprint or has uploaded certificate
        const hasCertificate = record?.is_certified_employee;
        return (
          <div className="flex items-center justify-center">
            {hasCertificate ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 size={20} className="text-green-600" />
                <Tag color="success">Certified</Tag>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <XCircle size={20} className="text-red-600" />
                <Tag color="error">Not Certified</Tag>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'E-Signature',
      key: 'certificate',
      dataIndex: 'esignature',
      width: 160,
      align: 'center',
      defaultVisible: true,
      sortable: false,
      render: (_, record) => (
        <div className="flex flex-col gap-1">
          <UserCertificateInfo
            userId={record.employee_id}
            username={
              record.employee_name || `${record.first_name || ''} ${record.last_name || ''}`
            }
            refreshTrigger={certificateRefresh}
          />
          <Button
            type="link"
            size="small"
            icon={<SafetyCertificateOutlined />}
            onClick={() => handleViewSignatures(record)}
          >
            View Signatures
          </Button>
        </div>
      ),
    },
    ...(user_Configuration_access?.create_access || user_Configuration_access?.update_access || true
      ? [
          {
            title: 'Training',
            key: 'training',
            dataIndex: 'training',
            width: 140,
            align: 'center',
            defaultVisible: true,
            sortable: false,
            render: (_, record) => (
              <div className="flex flex-col gap-1 w-full">
                {record.is_certified_employee ? (
                  <div className="flex items-center justify-center gap-1 text-green-600 font-large">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                ) : (
                  <Button
                    type="primary"
                    size="small"
                    icon={<SafetyCertificateOutlined />}
                    onClick={() => handleCertifyTraining(record)}
                    className="w-full bg-green-600 hover:bg-green-700 border-green-600"
                  >
                    Certify
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
    ...detailsColumn,
    ...actionColumn,
  ];

  const [userShow, setuserShow] = useState(false);

  // Table configuration hook
  const { visibleColumns, filteredData, columnConfigMenu } = useTableConfig({
    tableKey: 'user_management',
    columns: allColumns,
    data: employees_list || [],
    searchValue: searchvalue,
  });

  // ----------------------Delete Modal-------------------------

  // =======
  const [form] = Form.useForm();

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    const userFileData = new FormData();
    userFileData.append('file', selectedFile);
    //  add_bulk_user(userFileData, success_add_User);
  };

  const handle_submit_add_name = () => {
    if (add_index === '1') {
      add_role(
        {
          role_name: add_name,
        },
        success_config
      );
    }
    if (add_index === '2') {
      add_department(
        {
          Department_name: add_name,
        },
        success_config
      );
    }
    if (add_index === '3') {
      add_designation(
        {
          designation: add_name,
        },
        success_config
      );
    }
    if (add_index === '9') {
      bp_add_location_data(
        {
          location_name: add_name,
        },
        success_config
      );
    }
    if (add_index === '5') {
      bp_save_employee_shift(
        {
          shift: add_shift,
        },
        success_config
      );
    }
  };

  const success_config = (res) => {
    if (res.status === 'success') {
      toast.success(res.message);

      if (add_index === '1') {
        get_roles(success_set_original_role_list);
      }
      if (add_index === '2') {
        get_departments(success_set_department);
      }
      if (add_index === '3') {
        get_designations(success_set_designation_list);
      }
      if (add_index === '9') {
        get_warehouse_area(success_get_location);
      }
      if (add_index === '5') {
        get_shift(success_get_shift);
      }
      setIsModalOpen(false);
      set_add_name('');
    } else if (res.status === 'failed') {
      toast.warning(res.message);
    }
  };

  const [emailError, setEmailError] = useState('');
  const [userIdError, setUserIdError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [contactError, setContactError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [departmentError, setDepartmentError] = useState('');
  const [designationError, setDesignationError] = useState('');
  const [roleError, setRoleError] = useState('');
  const [shiftError, setShiftError] = useState('');

  return (
    <>
      <style>
        {`
          .custom-user-table .ant-table-cell-fix-right {
            background: ${tableBgColor} !important;
            box-shadow: -2px 0 8px rgba(0, 0, 0, ${isDark ? '0.3' : '0.08'});
          }

          .custom-user-table .ant-table-thead > tr > th.ant-table-cell-fix-right {
            background: ${tableHeaderBgColor} !important;
          }

          .custom-user-table .ant-table-cell-fix-right:hover {
            background: ${tableRowHoverBg} !important;
          }

          .custom-user-table .ant-table-thead > tr > th {
            background: ${tableHeaderBgColor} !important;
            font-weight: 600;
            color: ${textColor} !important;
          }

          .custom-user-table .ant-table-tbody > tr > td {
            background: ${tableBgColor} !important;
            color: ${textColor} !important;
            border-color: ${borderColor} !important;
          }

          .custom-user-table .ant-table-tbody > tr:hover > td {
            background: ${tableRowHoverBg} !important;
          }

          .custom-user-table .ant-table {
            background: ${tableBgColor} !important;
          }

          .custom-user-table .ant-table-container {
            border-color: ${borderColor} !important;
          }

          .custom-user-table .ant-table-bordered .ant-table-cell {
            border-color: ${borderColor} !important;
          }
        `}
      </style>
      <Card
        className="border border-gray-300 shadow-md"
        // When embedded as a tab inside UserManagementDashboard.js, the tab label
        // itself already shows "Users" — a second "User Config" title here is
        // redundant. Only show it on the standalone /userConfig route.
        title={
          isEmbedded ? undefined : <span style={{ color: textColor }}>{t('User Config')}</span>
        }
        style={{ backgroundColor: cardBgColor }}
        extra={
          <div className="flex items-center gap-3">
            {user_Configuration_access?.create_access && (
              <Tooltip title={userShow ? 'Close Form' : 'Add User'} placement="bottom">
                {userShow ? (
                  <Icons.X
                    className="w-7 h-7 hover:scale-110 transition-transform cursor-pointer"
                    onClick={() => {
                      setuserShow(false);
                      set_edit_val(false);
                    }}
                  />
                ) : (
                  <Icons.Plus
                    className="w-7 h-7 hover:scale-110 transition-transform cursor-pointer"
                    onClick={() => setuserShow(true)}
                  />
                )}
              </Tooltip>
            )}
            <TableColumnConfig menu={columnConfigMenu} />
            <ReusableSearchInput
              placeholder="Search here..."
              width={220}
              mt={0}
              mb={0}
              onSearch={(value) => setSearchvalue(value)}
              onChange={(e) => setSearchvalue(e.target.value)}
            />
          </div>
        }
      >
        <>
          {/* Admin Training Expiry Alert */}
          {globalExpiringTrainings?.length > 0 && (
            <Alert
              message={
                <span>
                  <WarningOutlined style={{ marginRight: 8 }} />
                  Training Alert: {globalExpiringTrainings.length} training
                  {globalExpiringTrainings.length > 1 ? 's' : ''} expiring in the next 4 weeks
                </span>
              }
              description={
                <span>
                  Multiple employees have trainings that will expire soon. Click to view details.
                </span>
              }
              type="warning"
              showIcon
              closable
              action={
                <Button size="small" type="primary" onClick={() => setShowExpiringModal(true)}>
                  View Details
                </Button>
              }
              style={{ marginBottom: 16 }}
            />
          )}
          <div className="flex flex-col gap-2">
            {userShow && (
              <div
                className="rounded-xl border mb-5"
                style={{
                  backgroundColor: formBgColor,
                  borderColor: accentColor,
                  borderLeftWidth: 4,
                  borderLeftStyle: 'solid',
                  padding: '20px 24px 12px',
                }}
              >
                {/* Form header */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-1.5 rounded-md" style={{ backgroundColor: accentBg }}>
                    {edit_val ? (
                      <Icons.Pencil className="w-4 h-4" style={{ color: accentColor }} />
                    ) : (
                      <Icons.Plus className="w-4 h-4" style={{ color: accentColor }} />
                    )}
                  </div>
                  <span className="text-sm font-semibold" style={{ color: accentColor }}>
                    {edit_val ? 'Edit User' : 'Add New User'}
                  </span>
                  {edit_val && (
                    <Tag color="warning" className="ml-2 text-xs">
                      Editing
                    </Tag>
                  )}
                </div>

                <Form form={form} onFinish={handleSubmit}>
                  <Row gutter={[10, 4]}>
                    <Col span={12}>
                      <label className="font-medium mb-1">
                        User Id <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <Input
                        size="large"
                        type="text"
                        name="user"
                        placeholder="Enter User Id"
                        value={userId}
                        onChange={(e) => {
                          setuserId(e.target.value);
                          if (userIdError) setUserIdError('');
                        }}
                        status={userIdError ? 'error' : ''}
                      />
                      {userIdError && (
                        <div style={{ color: 'red', fontSize: 12 }}>{userIdError}</div>
                      )}
                    </Col>
                    <Col span={12}>
                      <label className="font-medium mb-1">
                        First Name <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <Input
                        size="large"
                        type="text"
                        name="first_name"
                        placeholder="Enter First Name"
                        value={first_name}
                        onChange={handlefirst_name}
                        status={firstNameError ? 'error' : ''}
                      />
                      {firstNameError && (
                        <div style={{ color: 'red', fontSize: 12 }}>{firstNameError}</div>
                      )}
                    </Col>
                    <Col span={12}>
                      <label className="font-medium mb-1">
                        Last Name <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <Input
                        size="large"
                        type="text"
                        name="last_name"
                        placeholder="Enter Last Name"
                        value={last_name}
                        onChange={handlelast_name}
                        status={lastNameError ? 'error' : ''}
                      />
                      {lastNameError && (
                        <div style={{ color: 'red', fontSize: 12 }}>{lastNameError}</div>
                      )}
                    </Col>
                    <Col span={12}>
                      <label className="font-medium mb-1">
                        Email <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <Input
                        size="large"
                        type="email"
                        name="email"
                        placeholder="Enter Email"
                        value={email}
                        onChange={(e) => {
                          handleEmail(e);
                          const emailValue = e.target.value;
                          if (
                            !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailValue) &&
                            emailValue !== ''
                          ) {
                            setEmailError('Enter valid email');
                          } else {
                            setEmailError('');
                          }
                        }}
                        status={emailError ? 'error' : ''}
                      />
                      {emailError && <div style={{ color: 'red', fontSize: 12 }}>{emailError}</div>}
                    </Col>
                    <Col span={12}>
                      <label className="font-medium mb-1">
                        Phone <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <Input
                        size="large"
                        type="text"
                        name="contact"
                        placeholder="Enter Phone Number"
                        value={contact}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value) && value.length <= 10) {
                            handleContact(e);
                          }
                        }}
                        status={contactError ? 'error' : ''}
                      />
                      {contactError && (
                        <div style={{ color: 'red', fontSize: 12 }}>{contactError}</div>
                      )}
                    </Col>
                    <Col span={12}>
                      <label className="font-medium mb-1">
                        Working Location <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        size="large"
                        value={location}
                        style={{ width: '100%' }}
                        placeholder="Select Area Location"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children.toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(value) => {
                          set_location(value);
                          if (locationError) setLocationError('');
                        }}
                        status={locationError ? 'error' : ''}
                      >
                        {location_list?.map((item) => (
                          <Select.Option
                            key={item.warehouse_area_id}
                            value={item.warehouse_area_id}
                          >
                            {item.name}
                          </Select.Option>
                        ))}
                      </Select>
                      {locationError && (
                        <div style={{ color: 'red', fontSize: 12 }}>{locationError}</div>
                      )}
                    </Col>
                    <Col span={12}>
                      <label className="font-medium mb-1">
                        Department <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <Space.Compact style={{ width: '100%' }}>
                        <Select
                          allowClear
                          showSearch
                          size="large"
                          value={department_id}
                          style={{ width: '100%' }}
                          placeholder="Select Department"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                          }
                          onChange={(value) => {
                            setDepartment(value);
                            if (departmentError) setDepartmentError('');
                          }}
                          status={departmentError ? 'error' : ''}
                        >
                          {department_list?.map((item) => (
                            <Select.Option key={item?.id} value={item?.id}>
                              {item?.department_name}
                            </Select.Option>
                          ))}
                        </Select>
                        <Tooltip title="Add Department">
                          <Button
                            type="dashed"
                            size="large"
                            icon={<PlusOutlined />}
                            onClick={() => handle_add_icon('2')}
                          />
                        </Tooltip>
                      </Space.Compact>
                      {departmentError && (
                        <div style={{ color: 'red', fontSize: 12 }}>{departmentError}</div>
                      )}
                    </Col>
                    <Col span={12}>
                      <label className="font-medium mb-1">
                        Designation <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <Space.Compact style={{ width: '100%' }}>
                        <Select
                          allowClear
                          showSearch
                          size="large"
                          value={designation_id}
                          style={{ width: '100%' }}
                          placeholder="Select Designation"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                          }
                          onChange={(value) => {
                            setDesignation(value);
                            if (designationError) setDesignationError('');
                          }}
                          status={designationError ? 'error' : ''}
                        >
                          {designation_list?.map((item) => (
                            <Select.Option key={item?.designation_id} value={item?.designation_id}>
                              {item?.designation}
                            </Select.Option>
                          ))}
                        </Select>
                        <Tooltip title="Add Designation">
                          <Button
                            type="dashed"
                            size="large"
                            icon={<PlusOutlined />}
                            onClick={() => handle_add_icon('3')}
                          />
                        </Tooltip>
                      </Space.Compact>
                      {designationError && (
                        <div style={{ color: 'red', fontSize: 12 }}>{designationError}</div>
                      )}
                    </Col>
                    <Col span={12}>
                      <label className="font-medium mb-1">
                        Shift <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <Space.Compact style={{ width: '100%' }}>
                        <Select
                          mode="multiple"
                          allowClear
                          showSearch
                          size="large"
                          value={shift}
                          style={{ width: '100%' }}
                          placeholder="Select Shift"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                          }
                          onChange={(value) => {
                            setShift(value);
                            if (shiftError) setShiftError('');
                          }}
                          status={shiftError ? 'error' : ''}
                        >
                          {shift_list?.map((item) => (
                            <Select.Option key={item?.shift_id} value={item?.shift_id}>
                              {item?.shift}
                            </Select.Option>
                          ))}
                        </Select>
                        <Tooltip title="Add Shift">
                          <Button
                            type="dashed"
                            size="large"
                            icon={<PlusOutlined />}
                            onClick={() => handle_add_icon('5')}
                          />
                        </Tooltip>
                      </Space.Compact>
                      {shiftError && <div style={{ color: 'red', fontSize: 12 }}>{shiftError}</div>}
                    </Col>
                    <Col span={12}>
                      <label className="font-medium mb-1">
                        Role <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <Space.Compact style={{ width: '100%' }}>
                        <Select
                          mode="multiple"
                          allowClear
                          showSearch
                          size="large"
                          value={role_id}
                          style={{ width: '100%' }}
                          placeholder="Select Role"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                          }
                          onChange={(value) => {
                            setRole(value);
                            if (roleError) setRoleError('');
                          }}
                          status={roleError ? 'error' : ''}
                        >
                          {original_role_list?.map((item) => (
                            <Select.Option key={item?.role_id} value={item?.role_id}>
                              {item?.role}
                            </Select.Option>
                          ))}
                        </Select>
                        <Tooltip title="Add Role">
                          <Button
                            type="dashed"
                            size="large"
                            icon={<PlusOutlined />}
                            onClick={() => handle_add_icon('1')}
                          />
                        </Tooltip>
                      </Space.Compact>
                      {roleError && <div style={{ color: 'red', fontSize: 12 }}>{roleError}</div>}
                    </Col>
                    <Col span={12}>
                      <label className="font-medium mb-1">E-Signature Certificate (Optional)</label>
                      <Space.Compact style={{ width: '100%' }}>
                        <input
                          ref={certificateInputRef}
                          type="file"
                          accept=".pem,.crt,.cer"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const validTypes = ['.pem', '.crt', '.cer'];
                              const fileExtension = file.name
                                .substring(file.name.lastIndexOf('.'))
                                .toLowerCase();
                              if (validTypes.includes(fileExtension)) {
                                setCertificateFile(file);
                                toast.success(`Certificate file selected: ${file.name}`);
                              } else {
                                toast.error(
                                  'Please select a valid certificate file (.pem, .crt, or .cer)'
                                );
                                e.target.value = null;
                              }
                            }
                          }}
                          style={{
                            padding: '8px',
                            border: `1px solid ${borderColor}`,
                            borderRadius: '4px',
                            width: '100%',
                            fontSize: '14px',
                            backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff',
                            color: textColor,
                          }}
                        />
                        {certificateFile && (
                          <Tooltip title="Clear certificate">
                            <Button
                              type="text"
                              size="large"
                              onClick={() => {
                                setCertificateFile(null);
                                if (certificateInputRef.current)
                                  certificateInputRef.current.value = null;
                                toast.info('Certificate file cleared');
                              }}
                              icon={<CloseOutlined className="text-red-500" />}
                            />
                          </Tooltip>
                        )}
                      </Space.Compact>
                      {certificateFile && (
                        <div className="text-xs text-blue-500 mt-1">
                          <SafetyCertificateOutlined style={{ marginRight: '4px' }} />
                          Selected: {certificateFile.name}
                        </div>
                      )}
                    </Col>
                    <Col span={24} style={{ marginTop: 16 }}>
                      <div className="flex justify-end gap-2">
                        <Tooltip title="Reset">
                          <Button
                            icon={<Icons.Undo2 className="w-4 h-4" />}
                            onClick={() => {
                              resetForm();
                              setuserShow(false);
                              set_edit_val(false);
                            }}
                          />
                        </Tooltip>
                        {(() => {
                          // Button stays enabled/visible at all times; handleSubmit's own
                          // required-field checks show a toast naming the specific missing
                          // field, which is more actionable than a disabled button.
                          const enabledStyle = edit_val
                            ? { backgroundColor: '#f59e0b', borderColor: '#f59e0b' }
                            : {};
                          return (
                            <Button
                              type="primary"
                              htmlType="button"
                              onClick={handleSubmit}
                              icon={
                                edit_val ? (
                                  <Icons.Check className="w-4 h-4" />
                                ) : (
                                  <Icons.Plus className="w-4 h-4" />
                                )
                              }
                              style={enabledStyle}
                            >
                              {edit_val ? 'Update' : 'Add'}
                            </Button>
                          );
                        })()}
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            )}

            {userShow && <Divider className="my-2" />}

            <TableContainer
              size="small"
              bordered
              columns={visibleColumns}
              data={filteredData}
              scrollX={1910}
              height="calc(100vh - 605px)"
              rowKey="employee_id"
              className="custom-user-table w-full"
              sticky
            />

            {/* Separate Pagination Component */}
            {!searchvalue && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={totalUsers}
                  showSizeChanger
                  showTotal={(total) => `Total ${total} users`}
                  onChange={(page, pageSize) => {
                    setCurrentPage(page);
                    setPageSize(pageSize);
                  }}
                  onShowSizeChange={(current, size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                    get_lists(1);
                  }}
                  pageSizeOptions={['10', '15', '20', '50', '100']}
                />
              </div>
            )}

            {/* Client-side pagination for search results */}
            {searchvalue && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                <Pagination
                  pageSize={15}
                  total={filteredData?.length || 0}
                  showSizeChanger
                  showTotal={(total) => `Total ${total} users`}
                  pageSizeOptions={['10', '15', '20', '50', '100']}
                />
              </div>
            )}
          </div>

          <Modal
            title={`Add ${
              add_index === '1'
                ? 'Role'
                : add_index === '2'
                ? 'Department'
                : add_index === '3'
                ? 'Designation'
                : add_index === '4'
                ? 'Group'
                : add_index === '5'
                ? 'Shift'
                : add_index === '9' && 'location'
            }`}
            open={isModalOpen}
            okButtonProps={{
              className: 'bg-blue-600 text-white',
              disabled: add_index === '5' ? add_shift.length === 0 : add_name.length === 0,
            }}
            onOk={handle_submit_add_name}
            onCancel={handleCancel}
            okText="Add"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 items-center">
              <div>
                <label id="name" className="font-medium mb-1" style={{ color: labelColor }}>
                  {`${
                    add_index === '1'
                      ? 'Role'
                      : add_index === '2'
                      ? 'Department'
                      : add_index === '3'
                      ? 'Designation'
                      : add_index === '4'
                      ? 'Group'
                      : add_index === '5'
                      ? 'Shift'
                      : add_index === '9' && 'Location'
                  } : `}
                </label>
              </div>
              <div>
                {add_index === '5' ? (
                  <TimePicker.RangePicker
                    onChange={(e, e1) => {
                      handle_add_shift(e1);
                    }}
                  />
                ) : (
                  <Input
                    id="name"
                    type="text"
                    name="text"
                    size="small"
                    placeholder={`Enter ${
                      add_index === '1'
                        ? 'Role Name'
                        : add_index === '2'
                        ? 'Department Name'
                        : add_index === '3'
                        ? 'Designation Name'
                        : add_index === '4'
                        ? 'Group Name'
                        : add_index === '5'
                        ? 'Select Shift'
                        : add_index === '9' && 'location name'
                    }`}
                    className="w-full"
                    required
                    value={add_name}
                    onChange={handle_add_name}
                  />
                )}
              </div>
            </div>
          </Modal>

          {/* E-Signature History Modal */}
          <UserSignatureHistory
            open={showSignatureHistory}
            onClose={() => {
              setShowSignatureHistory(false);
              setSelectedUser(null);
            }}
            userId={selectedUser?.employee_id}
            username={
              selectedUser?.employee_name ||
              `${selectedUser?.first_name || ''} ${selectedUser?.last_name || ''}`
            }
          />

          {/* Global Expiring Trainings Modal */}
          <Modal
            title={
              <span style={{ fontSize: '18px' }}>
                <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                Trainings Expiring in Next 4 Weeks
              </span>
            }
            open={showExpiringModal}
            onCancel={() => setShowExpiringModal(false)}
            width="80%"
            footer={[
              <Button key="close" onClick={() => setShowExpiringModal(false)}>
                Close
              </Button>,
            ]}
          >
            <Table
              columns={[
                {
                  title: 'Employee',
                  dataIndex: 'employee_name',
                  key: 'employee_name',
                  width: '20%',
                  render: (text, record) => (
                    <span>
                      {record.employee_name}
                      <br />
                      <small style={{ color: '#888' }}>ID: {record.employee_id}</small>
                    </span>
                  ),
                },
                {
                  title: 'Department',
                  dataIndex: 'department',
                  key: 'department',
                  width: '15%',
                },
                {
                  title: 'Course Name',
                  dataIndex: 'course_name',
                  key: 'course_name',
                  width: '25%',
                },
                {
                  title: 'Training Type',
                  dataIndex: 'training_type',
                  key: 'training_type',
                  width: '15%',
                  render: (text) => <Tag color="blue">{text}</Tag>,
                },
                {
                  title: 'Expiry Date',
                  dataIndex: 'expiry_date',
                  key: 'expiry_date',
                  width: '15%',
                  render: (date) => {
                    const daysLeft = toAppMoment(date)?.diff(toAppMoment(), 'days');
                    return (
                      <span>
                        {formatDate(date)}
                        <br />
                        <Tag color={daysLeft <= 7 ? 'red' : 'orange'}>{daysLeft} days left</Tag>
                      </span>
                    );
                  },
                },
                {
                  title: 'Action',
                  key: 'action',
                  width: '10%',
                  render: (_, record) => {
                    const employee = employees_list.find(
                      (emp) => emp.employee_id === record.employee_id
                    );
                    return (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          if (employee) {
                            setShowExpiringModal(false);
                            // handleViewTrainings(employee);
                          }
                        }}
                      >
                        View Details
                      </Button>
                    );
                  },
                },
              ]}
              dataSource={globalExpiringTrainings}
              rowKey={(record) => `${record.employee_id}-${record.course_id}`}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              size="small"
            />
          </Modal>

          {/* Training Details Drawer */}
          <Drawer
            title={
              <span style={{ fontSize: '18px' }}>
                Training Details -{' '}
                {selectedEmployee?.first_name || selectedEmployee?.employee_name?.split(' ')[0]}{' '}
                {selectedEmployee?.last_name || selectedEmployee?.employee_name?.split(' ')[1]}
              </span>
            }
            placement="right"
            onClose={handleTrainingDrawerClose}
            open={trainingDrawerVisible}
            width="80%"
            bodyStyle={{ paddingBottom: 80 }}
          >
            {trainingDrawerLoading ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" tip="Loading training records..." />
              </div>
            ) : (
              <>
                {/* Training Statistics */}
                {trainingStats && (
                  <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={6}>
                      <Card>
                        <Statistic
                          title="Total Trainings"
                          value={trainingStats.total_trainings}
                          prefix={<SafetyCertificateOutlined />}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card>
                        <Statistic
                          title="Valid Trainings"
                          value={trainingStats.valid_trainings}
                          valueStyle={{ color: '#3f8600' }}
                          prefix={<CheckCircleOutlined />}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card>
                        <Statistic
                          title="Expiring Soon"
                          value={trainingStats.expiring_soon}
                          valueStyle={{ color: '#faad14' }}
                          prefix={<WarningOutlined />}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card>
                        <Statistic
                          title="Expired"
                          value={trainingStats.expired_trainings}
                          valueStyle={{ color: '#cf1322' }}
                          prefix={<CloseCircleOutlined />}
                        />
                      </Card>
                    </Col>
                  </Row>
                )}

                {/* Operation-Relevant Training Information */}
                <Card>
                  <Tabs defaultActiveKey="1">
                    <Tabs.TabPane tab="Valid Trainings" key="1">
                      <Table
                        columns={[
                          {
                            title: 'Course Name',
                            dataIndex: ['course', 'course_name'],
                            key: 'course_name',
                            render: (text) => text || '-',
                          },
                          {
                            title: 'Type',
                            dataIndex: ['course', 'training_type'],
                            key: 'training_type',
                            render: (text) => <Tag color="blue">{text}</Tag>,
                          },
                          {
                            title: 'Completed On',
                            dataIndex: 'training_date',
                            key: 'training_date',
                            render: (date) => (date ? formatDate(date) : '-'),
                          },
                          {
                            title: 'Valid Until',
                            dataIndex: 'expiry_date',
                            key: 'expiry_date',
                            render: (date) => {
                              if (!date) return <Tag color="green">No Expiry</Tag>;
                              const daysLeft = toAppMoment(date)?.diff(toAppMoment(), 'days');
                              const isExpiring = daysLeft <= 30 && daysLeft > 0;
                              const isExpired = daysLeft < 0;

                              return (
                                <span>
                                  {formatDate(date)}
                                  {isExpiring && (
                                    <Tag color="orange" style={{ marginLeft: 8 }}>
                                      {daysLeft} days left
                                    </Tag>
                                  )}
                                  {isExpired && (
                                    <Tag color="red" style={{ marginLeft: 8 }}>
                                      EXPIRED
                                    </Tag>
                                  )}
                                </span>
                              );
                            },
                          },
                          {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            render: (status) => {
                              const color = statusColors[status] || 'default';
                              return <Badge status={color} text={status} />;
                            },
                          },
                        ]}
                        dataSource={trainingRecords.filter((r) => r.status === 'COMPLETED')}
                        rowKey="id"
                        pagination={false}
                        size="small"
                      />
                    </Tabs.TabPane>
                    <Tabs.TabPane
                      tab={
                        <span>
                          Action Required
                          {(trainingStats?.expiring_soon || 0) +
                            (trainingStats?.expired_trainings || 0) >
                            0 && (
                            <Badge
                              count={
                                (trainingStats?.expiring_soon || 0) +
                                (trainingStats?.expired_trainings || 0)
                              }
                              style={{ marginLeft: 8 }}
                            />
                          )}
                        </span>
                      }
                      key="2"
                    >
                      {trainingRecords.filter((record) => {
                        const daysUntilExpiry = toAppMoment(record.expiry_date)?.diff(toAppMoment(), 'days');
                        return (
                          (daysUntilExpiry <= 30 && daysUntilExpiry >= 0) ||
                          record.status === 'EXPIRED'
                        );
                      }).length > 0 ? (
                        <>
                          {trainingRecords.filter((record) => {
                            const daysUntilExpiry = toAppMoment(record.expiry_date)?.diff(
                              toAppMoment(),
                              'days'
                            );
                            return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
                          }).length > 0 && (
                            <Alert
                              message="Expiring Soon"
                              description={
                                <ul style={{ marginBottom: 0 }}>
                                  {trainingRecords
                                    .filter((record) => {
                                      const daysUntilExpiry = toAppMoment(record.expiry_date)?.diff(
                                        toAppMoment(),
                                        'days'
                                      );
                                      return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
                                    })
                                    .map((record, index) => (
                                      <li key={index}>
                                        <strong>{record.course?.course_name}</strong> - Expires on{' '}
                                        {formatDate(record.expiry_date)} (
                                        <span style={{ color: 'orange', fontWeight: 'bold' }}>
                                          {toAppMoment(record.expiry_date)?.diff(toAppMoment(), 'days')} days
                                          left
                                        </span>
                                        )
                                      </li>
                                    ))}
                                </ul>
                              }
                              type="warning"
                              showIcon
                              style={{ marginBottom: 16 }}
                            />
                          )}
                          {trainingRecords.filter((record) => record.status === 'EXPIRED').length >
                            0 && (
                            <Alert
                              message="Expired - Renewal Required"
                              description={
                                <ul style={{ marginBottom: 0 }}>
                                  {trainingRecords
                                    .filter((record) => record.status === 'EXPIRED')
                                    .map((record, index) => (
                                      <li key={index}>
                                        <strong>{record.course?.course_name}</strong> - Expired on{' '}
                                        {formatDate(record.expiry_date)}
                                      </li>
                                    ))}
                                </ul>
                              }
                              type="error"
                              showIcon
                            />
                          )}
                        </>
                      ) : (
                        <Empty description="No action required - All trainings are valid" />
                      )}
                    </Tabs.TabPane>
                  </Tabs>
                </Card>
              </>
            )}
          </Drawer>

          {/* Training Certification Modal */}
          <Modal
            title={
              <div className="flex items-center gap-2">
                <SafetyCertificateOutlined
                  className="text-green-600"
                  style={{ fontSize: '24px' }}
                />
                <span className="text-lg font-semibold" style={{ color: textColor }}>
                  Employee Training Certification
                </span>
              </div>
            }
            open={certificationModalVisible}
            onCancel={() => {
              setCertificationModalVisible(false);
              certificationForm.resetFields();
            }}
            onOk={handleCertificationSubmit}
            width={700}
            centered
            styles={{
              header: { backgroundColor: cardBgColor, borderBottom: `1px solid ${borderColor}` },
              body: {
                backgroundColor: cardBgColor,
                maxHeight: 'calc(100vh - 200px)',
                overflowY: 'auto',
                overflowX: 'hidden',
              },
              content: { backgroundColor: cardBgColor },
            }}
            okText="Certify Training"
            cancelText="Cancel"
            okButtonProps={{
              className: 'bg-green-600 hover:bg-green-700 border-green-600',
            }}
          >
            {certificationEmployee && (
              <div
                className="mb-4 p-3 rounded-lg border"
                style={{
                  backgroundColor: isDark ? defaultColors.palette.gray[700] : '#eff6ff',
                  borderColor: isDark ? defaultColors.palette.gray[600] : '#bfdbfe',
                }}
              >
                <Row gutter={[16, 8]}>
                  <Col span={12}>
                    <p className="text-xs mb-0" style={{ color: secondaryTextColor }}>
                      Employee Name
                    </p>
                    <p className="font-semibold text-sm mb-0" style={{ color: textColor }}>
                      {certificationEmployee.employee_name}
                    </p>
                  </Col>
                  <Col span={12}>
                    <p className="text-xs mb-0" style={{ color: secondaryTextColor }}>
                      Employee ID
                    </p>
                    <p className="font-semibold text-sm mb-0" style={{ color: textColor }}>
                      {certificationEmployee.username}
                    </p>
                  </Col>
                  <Col span={12}>
                    <p className="text-xs mb-0" style={{ color: secondaryTextColor }}>
                      Department
                    </p>
                    <p className="font-semibold text-sm mb-0" style={{ color: textColor }}>
                      {certificationEmployee.department || 'N/A'}
                    </p>
                  </Col>
                  <Col span={12}>
                    <p className="text-xs mb-0" style={{ color: secondaryTextColor }}>
                      Designation
                    </p>
                    <p className="font-semibold text-sm mb-0" style={{ color: textColor }}>
                      {certificationEmployee.designation || 'N/A'}
                    </p>
                  </Col>
                </Row>
              </div>
            )}

            <Form form={certificationForm} layout="vertical">
              <Row gutter={[16, 8]}>
                <Col span={24}>
                  <Form.Item
                    name="course_id"
                    label="Training Course (Optional)"
                    style={{ marginBottom: 12 }}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select training course"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children.toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {trainingCoursesList?.map((course) => (
                        <Select.Option key={course.id} value={course.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{course.course_name}</span>
                            <span className="text-xs text-gray-500">
                              {course.course_code} - {course.training_type}
                            </span>
                          </div>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="training_date"
                    label="Training Date"
                    rules={[{ required: true, message: 'Required' }]}
                    style={{ marginBottom: 12 }}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      format="DD-MM-YYYY"
                      placeholder="Select date"
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="completion_date"
                    label="Completion Date"
                    rules={[{ required: true, message: 'Required' }]}
                    style={{ marginBottom: 12 }}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      format="DD-MM-YYYY"
                      placeholder="Select date"
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="assessment_score"
                    label="Assessment Score (%)"
                    rules={[
                      { required: true, message: 'Required' },
                      {
                        type: 'number',
                        min: 0,
                        max: 100,
                        message: 'Score: 0-100',
                      },
                    ]}
                    style={{ marginBottom: 12 }}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} max={100} placeholder="0-100" />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="certificate_number"
                    label="Certificate Number (Optional)"
                    style={{ marginBottom: 12 }}
                  >
                    <Input placeholder="Certificate number" />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item name="remarks" label="Remarks (Optional)" style={{ marginBottom: 8 }}>
                    <Input.TextArea rows={2} placeholder="Additional notes" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            <Alert
              message="Certification Note"
              description="This certification will be recorded in the employee's training history. Ensure all information is accurate."
              type="info"
              showIcon
              style={{ marginTop: 8 }}
            />
          </Modal>
        </>
      </Card>

      {/* ── User Details Drawer ── */}
      <Drawer
        title={
          <span style={{ fontWeight: 700 }}>User Details — {detailRecord?.employee_name}</span>
        }
        placement="right"
        width={420}
        open={!!detailRecord}
        onClose={() => setDetailRecord(null)}
        mask={false}
        zIndex={2001}
      >
        {detailRecord && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Shifts */}
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: 8,
                }}
              >
                Shifts
              </div>
              {detailRecord.shifts_names?.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {detailRecord.shifts_names.map((name, i) => (
                    <Tag key={i} color="blue" style={{ margin: 0 }}>
                      {name}
                    </Tag>
                  ))}
                </div>
              ) : detailRecord.shift ? (
                <Tag color="blue">{detailRecord.shift}</Tag>
              ) : (
                <span style={{ fontSize: 13, color: '#9ca3af' }}>No shifts assigned</span>
              )}
            </div>

            {/* Locations */}
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: 8,
                }}
              >
                Locations
              </div>
              {detailRecord.locations_names?.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {detailRecord.locations_names.map((name, i) => (
                    <Tag key={i} color="green" style={{ margin: 0 }}>
                      {name}
                    </Tag>
                  ))}
                </div>
              ) : detailRecord.location ? (
                <Tag color="green">{detailRecord.location}</Tag>
              ) : (
                <span style={{ fontSize: 13, color: '#9ca3af' }}>No locations assigned</span>
              )}
            </div>

            <Divider style={{ margin: '4px 0' }} />

            {/* Other info */}
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Username">{detailRecord.username}</Descriptions.Item>
              <Descriptions.Item label="Email">{detailRecord.email}</Descriptions.Item>
              <Descriptions.Item label="Contact">{detailRecord.contact}</Descriptions.Item>
              <Descriptions.Item label="Department">{detailRecord.department}</Descriptions.Item>
              <Descriptions.Item label="Designation">{detailRecord.designation}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>
    </>
  );
};

export default UserConfig2;
